import { cn } from "@/lib/utils";

import { ENTITY_TYPE_LABELS } from "../../constants";
import type { Interaction } from "../../hooks/useSandboxPersonas";
import type { SandboxEntityType } from "../../types/entities";
import type { InteractionType } from "../../types/interactions";

const ENTITY_BAR_CLASSES: Record<SandboxEntityType, string> = {
  artist: "bg-violet-400",
  genre: "bg-sky-400",
  venue: "bg-amber-400",
  event: "bg-emerald-400",
};

interface InfluenceBreakdownProps {
  interactions: Interaction[];
  getWeight: (entityType: SandboxEntityType, interactionType: InteractionType) => number;
  getDecayMultiplier: (interaction: Interaction) => number;
}

export function InfluenceBreakdown({
  interactions,
  getWeight,
  getDecayMultiplier,
}: InfluenceBreakdownProps) {
  const weightsByType: Record<SandboxEntityType, number> = {
    artist: 0,
    genre: 0,
    venue: 0,
    event: 0,
  };
  let totalWeight = 0;

  for (const i of interactions) {
    const ew = getWeight(i.entityType, i.interactionType) * getDecayMultiplier(i);
    weightsByType[i.entityType] += ew;
    totalWeight += ew;
  }

  const entries = (["artist", "genre", "venue", "event"] as SandboxEntityType[])
    .map((type) => ({
      type,
      pct: totalWeight > 0 ? (weightsByType[type] / totalWeight) * 100 : 0,
    }))
    .filter(({ pct }) => pct > 0);

  if (entries.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No weighted interactions.</p>
    );
  }

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Influence by type</h3>
      {entries.map(({ type, pct }) => (
        <div key={type} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {ENTITY_TYPE_LABELS[type]}s
            </span>
            <span className="font-medium tabular-nums">{pct.toFixed(1)}%</span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className={cn(
                "absolute h-full rounded-full transition-all",
                ENTITY_BAR_CLASSES[type],
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ))}
    </section>
  );
}
